import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, CheckCircle2, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { PlanetLogo } from "@/components/PlanetLogo";
import { GlowCard } from "@/components/GlowCard";
import { SiteFooter } from "@/components/SiteFooter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CONTACT, COMPANY } from "@/content/galaxy";
import { submitContactMessage } from "@/lib/lazyforms";

const initialForm = { name: "", email: "", subject: "", message: "", botcheck: "" };
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Kontakt() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Bitte gib deinen Namen ein.";
    if (!form.email.trim()) e.email = "Bitte gib eine E-Mail-Adresse ein.";
    else if (!emailRegex.test(form.email)) e.email = "Diese E-Mail-Adresse ist ungültig.";
    if (!form.subject.trim()) e.subject = "Bitte gib einen Betreff ein.";
    if (!form.message.trim()) e.message = "Bitte schreib uns eine Nachricht.";
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (status === "loading") return;
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      toast.error("Bitte fülle alle Felder aus.");
      return;
    }
    setStatus("loading");
    const res = await submitContactMessage(form);
    if (res.ok) {
      setStatus("success");
      toast.success(CONTACT.successTitle);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setStatus("idle");
      toast.error(res.message || CONTACT.errorMessage);
    }
  };

  const inputCls =
    "h-12 rounded-xl border-white/10 bg-white/5 text-white placeholder:text-white/35 focus-visible:ring-2 focus-visible:ring-[rgba(58,123,255,0.55)] focus-visible:ring-offset-0";

  return (
    <div className="relative min-h-screen overflow-hidden" data-testid="kontakt-page">
      <div className="relative z-20">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-4 sm:px-6">
          <Link to="/" data-testid="kontakt-logo-link" className="flex items-center gap-2">
            <PlanetLogo />
            <span className="font-display text-sm font-bold tracking-[0.14em] text-white">
              GALAXY <span className="text-gl-blue2">LEAGUE</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="kontakt-back-link"
            className="inline-flex items-center gap-2 text-xs tracking-[0.16em] text-white/65 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            ZURÜCK
          </Link>
        </div>
      </div>

      <section className="hero-glow relative">
        <div className="mx-auto max-w-3xl px-4 pb-16 pt-8 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <span className="inline-flex items-center rounded-full border border-gl-blue/40 bg-gl-blue/10 px-4 py-1.5 text-[11px] font-semibold tracking-[0.18em] text-gl-blue2">
              {CONTACT.badge}
            </span>
            <h1 className="mt-6 font-display text-4xl font-extrabold uppercase leading-tight tracking-tight text-white sm:text-5xl">
              {CONTACT.headline}
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base">
              {CONTACT.intro}
            </p>
          </motion.div>

          {status === "success" ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mx-auto mt-10 max-w-lg"
            >
              <GlowCard className="px-8 py-12 text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-brand-gradient glow-md">
                  <CheckCircle2 className="h-8 w-8 text-white" />
                </div>
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-wide text-white">
                  {CONTACT.successTitle}
                </h2>
                <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-white/70">
                  {CONTACT.successMessage}
                </p>
                <Link
                  to="/"
                  data-testid="kontakt-success-home-link"
                  className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-semibold tracking-wide text-white transition-colors duration-200 hover:bg-white/5"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Zurück zur Startseite
                </Link>
              </GlowCard>
            </motion.div>
          ) : (
            <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-12">
              {/* Contact details */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.05 }}
                className="lg:col-span-5"
              >
                <GlowCard className="h-full p-6">
                  <h2 className="mb-5 font-display text-sm font-bold uppercase tracking-[0.12em] text-white">
                    Direkt erreichen
                  </h2>
                  <div className="space-y-5 text-sm text-white/70">
                    <a
                      href={`mailto:${COMPANY.email}`}
                      data-testid="kontakt-email-link"
                      className="flex items-start gap-3 transition-colors hover:text-white"
                    >
                      <Mail className="mt-0.5 h-5 w-5 text-gl-blue2" />
                      <span>{COMPANY.email}</span>
                    </a>
                    <a
                      href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                      data-testid="kontakt-phone-link"
                      className="flex items-start gap-3 transition-colors hover:text-white"
                    >
                      <Phone className="mt-0.5 h-5 w-5 text-gl-blue2" />
                      <span>{COMPANY.phone}</span>
                    </a>
                    <div className="flex items-start gap-3">
                      <MapPin className="mt-0.5 h-5 w-5 text-gl-blue2" />
                      <span>
                        {COMPANY.legalName}
                        <br />
                        {COMPANY.street}
                        <br />
                        {COMPANY.zip} {COMPANY.city}, {COMPANY.country}
                      </span>
                    </div>
                  </div>
                </GlowCard>
              </motion.div>

              {/* Form */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="lg:col-span-7"
              >
                <GlowCard className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                    <input
                      type="text"
                      name="botcheck"
                      value={form.botcheck}
                      onChange={set("botcheck")}
                      tabIndex={-1}
                      autoComplete="off"
                      className="hidden"
                      aria-hidden="true"
                    />
                    <div>
                      <Label className="mb-2 block text-xs text-white/55">Name</Label>
                      <Input
                        name="name"
                        data-testid="kontakt-name-input"
                        value={form.name}
                        onChange={set("name")}
                        className={inputCls}
                      />
                      {errors.name && <FieldError>{errors.name}</FieldError>}
                    </div>
                    <div>
                      <Label className="mb-2 block text-xs text-white/55">E-Mail-Adresse</Label>
                      <Input
                        type="email"
                        name="email"
                        data-testid="kontakt-email-input"
                        placeholder="deine@email.at"
                        value={form.email}
                        onChange={set("email")}
                        className={inputCls}
                      />
                      {errors.email && <FieldError>{errors.email}</FieldError>}
                    </div>
                    <div>
                      <Label className="mb-2 block text-xs text-white/55">Betreff</Label>
                      <Input
                        name="subject"
                        data-testid="kontakt-subject-input"
                        value={form.subject}
                        onChange={set("subject")}
                        className={inputCls}
                      />
                      {errors.subject && <FieldError>{errors.subject}</FieldError>}
                    </div>
                    <div>
                      <Label className="mb-2 block text-xs text-white/55">Nachricht</Label>
                      <Textarea
                        name="message"
                        data-testid="kontakt-message-input"
                        rows={5}
                        value={form.message}
                        onChange={set("message")}
                        className="rounded-xl border-white/10 bg-white/5 text-white placeholder:text-white/35 focus-visible:ring-2 focus-visible:ring-[rgba(58,123,255,0.55)] focus-visible:ring-offset-0"
                      />
                      {errors.message && <FieldError>{errors.message}</FieldError>}
                    </div>
                    <button
                      type="submit"
                      data-testid="kontakt-submit-button"
                      disabled={status === "loading"}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-gradient px-7 py-4 text-sm font-semibold tracking-wide text-white glow-md transition-[filter,transform] duration-200 hover:brightness-110 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {status === "loading" && <Loader2 className="h-4 w-4 animate-spin" />}
                      {status === "loading" ? "Wird gesendet …" : CONTACT.submitLabel}
                    </button>
                  </form>
                </GlowCard>
              </motion.div>
            </div>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function FieldError({ children }) {
  return <p className="mt-2 text-xs text-[#FF6B85]">{children}</p>;
}
